import React, { useState, useEffect } from 'react';
import { Application, Agent } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, User, Mail, Phone, Globe, GraduationCap, Calendar, MapPin, FileText, Clock, CheckCircle, XCircle, AlertCircle, ThumbsUp, UserCheck, ShieldAlert, LogOut, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const statusConfig = {
  submitted: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Submitted' },
  under_review: { icon: AlertCircle, color: 'bg-blue-100 text-blue-800', label: 'Under Review' },
  additional_docs_required: { icon: HelpCircle, color: 'bg-orange-100 text-orange-800', label: 'Docs Required' },
  conditional_offer: { icon: HelpCircle, color: 'bg-cyan-100 text-cyan-800', label: 'Conditional Offer' },
  unconditional_offer: { icon: ThumbsUp, color: 'bg-teal-100 text-teal-800', label: 'Offer' },
  offer_accepted: { icon: UserCheck, color: 'bg-indigo-100 text-indigo-800', label: 'Offer Accepted' },
  approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Approved' },
  rejected: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejected' },
  visa_rejected: { icon: ShieldAlert, color: 'bg-red-200 text-red-900', label: 'Visa Rejected' },
  enrolled: { icon: CheckCircle, color: 'bg-purple-100 text-purple-800', label: 'Enrolled' },
  withdrawn: { icon: LogOut, color: 'bg-gray-200 text-gray-800', label: 'Withdrawn' }
};

const statusOptions = [
  'submitted', 'under_review', 'additional_docs_required', 'conditional_offer', 
  'unconditional_offer', 'offer_accepted', 'approved', 'rejected', 'visa_rejected', 
  'enrolled', 'withdrawn'
];

export default function ApplicationDetail() {
  const [applicationId, setApplicationId] = useState(null);
  const [application, setApplication] = useState(null);
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setApplicationId(id);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (applicationId) {
      fetchApplicationData();
    }
  }, [applicationId]);

  const fetchApplicationData = async () => {
    setIsLoading(true);
    try {
      const appData = await Application.get(applicationId);
      setApplication(appData);
      
      if (appData.agent_id) {
        const agentData = await Agent.get(appData.agent_id);
        setAgent(agentData);
      }
    } catch (error) {
      console.error("Failed to fetch application details:", error);
    }
    setIsLoading(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!applicationId) return;
    setIsUpdating(true);
    try {
      await Application.update(applicationId, { 
        status: newStatus,
        last_updated: new Date().toISOString()
      });
      setApplication(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Failed to update status. Please try again.");
    }
    setIsUpdating(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-1/3 mb-8" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <Skeleton className="h-64 w-full rounded-3xl" />
              <Skeleton className="h-48 w-full rounded-3xl" />
            </div>
            <div className="space-y-8">
              <Skeleton className="h-56 w-full rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-6 bg-gray-200 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Application Not Found</h2>
          <p className="text-gray-600 mb-6">Could not find the specified application.</p>
          <Link to={createPageUrl("UniversityApplications")}>
            <Button className="neumorphic rounded-2xl px-6 py-3">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Applications
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = statusConfig[application.status] || statusConfig.submitted;
  const StatusIcon = statusInfo.icon;

  return (
    <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to={createPageUrl("UniversityApplications")}>
            <Button className="neumorphic rounded-2xl p-3">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-neumorphic">Application Details</h1>
            <p className="text-gray-600">Complete information for {application.student_name}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Student Information */}
            <Card className="neumorphic rounded-3xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-semibold text-neumorphic flex items-center gap-3">
                    <User className="w-6 h-6 text-indigo-600" />
                    Student Information
                  </CardTitle>
                  <Badge className={`${statusInfo.color} border-0 rounded-xl px-4 py-2 font-medium`}>
                    <StatusIcon className="w-4 h-4 mr-2" />
                    {statusInfo.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 neumorphic-inset rounded-xl flex items-center justify-center">
                      <User className="w-6 h-6 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="font-semibold text-neumorphic text-lg">{application.student_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 neumorphic-inset rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p className="font-semibold text-neumorphic">{application.student_email}</p>
                    </div>
                  </div>
                  {application.student_phone && (
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 neumorphic-inset rounded-xl flex items-center justify-center">
                        <Phone className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-semibold text-neumorphic">{application.student_phone}</p>
                      </div>
                    </div>
                  )}
                  {application.nationality && (
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 neumorphic-inset rounded-xl flex items-center justify-center">
                        <Globe className="w-6 h-6 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Nationality</p>
                        <p className="font-semibold text-neumorphic">{application.nationality}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Program Information */}
            <Card className="neumorphic rounded-3xl">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-neumorphic flex items-center gap-3">
                  <GraduationCap className="w-6 h-6 text-indigo-600" />
                  Program Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Program Applied</p>
                    <p className="font-semibold text-neumorphic text-lg">{application.program_applied}</p>
                  </div>
                  {application.program_category && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Program Category</p>
                      <Badge className="bg-indigo-100 text-indigo-800 border-0">{application.program_category}</Badge>
                    </div>
                  )}
                  {application.application_type && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Application Type</p>
                      <Badge className="bg-purple-100 text-purple-800 border-0">{application.application_type}</Badge>
                    </div>
                  )}
                  {application.campus && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Campus</p>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-neumorphic">{application.campus}</span>
                      </div>
                    </div>
                  )}
                  {application.intake_date && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Intake Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-neumorphic">
                          {format(new Date(application.intake_date), 'MMMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {application.notes && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Additional Notes</p>
                    <div className="neumorphic-inset rounded-xl p-4">
                      <p className="text-neumorphic">{application.notes}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-8">
            {/* Status Management */}
            <Card className="neumorphic rounded-3xl">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-neumorphic">Status Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-2">Current Status</p>
                  <Badge className={`${statusInfo.color} border-0 rounded-xl px-4 py-2 font-medium text-base`}>
                    <StatusIcon className="w-4 h-4 mr-2" />
                    {statusInfo.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">Update Status</p>
                  <Select value={application.status} onValueChange={handleStatusUpdate} disabled={isUpdating}>
                    <SelectTrigger className="neumorphic-inset rounded-xl border-0 bg-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="neumorphic rounded-2xl border-0">
                      {statusOptions.map(status => {
                        const info = statusConfig[status];
                        return (
                          <SelectItem key={status} value={status}>
                            <div className="flex items-center gap-2">
                              <info.icon className="w-4 h-4" />
                              {info.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Agent Information */}
            {agent && (
              <Card className="neumorphic rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-neumorphic">Submitted By</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Agent Company</p>
                    <p className="font-semibold text-neumorphic">{agent.company_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact Person</p>
                    <p className="font-medium text-neumorphic">{agent.contact_person}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Agent Code</p>
                    <Badge className="bg-gray-100 text-gray-800 border-0 font-mono">{agent.agent_code}</Badge>
                  </div>
                  <Link to={createPageUrl(`AgentDetail?id=${agent.id}`)}>
                    <Button className="neumorphic rounded-xl w-full mt-4">
                      View Agent Profile
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Application Timeline */}
            <Card className="neumorphic rounded-3xl">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-neumorphic">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 neumorphic-inset rounded-full flex items-center justify-center">
                    <FileText className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div>
                    <p className="font-medium text-neumorphic">Application Submitted</p>
                    <p className="text-sm text-gray-500">
                      {application.created_at ? format(new Date(application.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                    </p>
                  </div>
                </div>
                {application.updated_at && application.updated_at !== application.created_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 neumorphic-inset rounded-full flex items-center justify-center">
                      <Clock className="w-4 h-4 text-orange-500" />
                    </div>
                    <div>
                      <p className="font-medium text-neumorphic">Last Updated</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(application.last_updated), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}